import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../hooks/useReduxToolkit';
import { getIdentityId } from '../../../../../features/realtime/realtime.selectors';
import {
  subscribeToLegacyMessage,
  subscribeToMessage,
} from '../../../../../features/realtime/realtime.subscribe';
import {
  RealtimeMessageType,
  RealtimeRole,
  type CallStartMessagePayload,
  type RealtimeMessage,
} from '../../../../../features/realtime/realtime.types';
import { type ApolloSession } from '../../../../../features/apolloSession/apolloSession.types';
import { update } from '../../../../../features/apolloSession/apolloSession.slice';
import { useRedirect } from '../../../../../hooks/useRedirect';
import { useEffect } from 'react';
import { useAppRouter } from '../../../../../hooks/useAppRouter';
import {
  WaitingRoomStep,
  waitingRoomTimer,
} from '../../../../../features/waitingRoom/waitingRoomTimer';
import { getPublicRuntimeConfig } from '../../../../../libs/init/getPublicRuntimeConfig';
import { useService } from '../../../../../hooks/useService';
import { type PatientCallService } from '../../../../../services/call/patientCall.service';
import { getIsGraphqlRoomQueriesEnabled } from '@doxyme/frontend/utils/isGraphqlRoomQueriesEnabled';
import { type RoomForPatient } from '../../../../../services/room/room.types';
import { useLazyGetRoomBySlugOGQuery } from '../../../../../services/waiting-room';
import {
  useLazyGetRoomForPatientQuery,
  mapOgRoomToV2ForPatient,
  PATIENT_ROOM_FEATURE_KEYS,
} from '../../../../../services/room';

type UsePatientWaitingRoomReturn = {
  room?: RoomForPatient | null;
};

export const usePatientWaitingRoom = (): UsePatientWaitingRoomReturn => {
  const identityId = useAppSelector(getIdentityId);
  const router = useAppRouter();
  const callService = useService('call') as PatientCallService;

  /**
   * Temporary solution to get the room to make call work until the WR stuff is done.
   */
  const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

  const [getRoomBySlug, { data: ogData }] = useLazyGetRoomBySlugOGQuery();
  const [getRoomForPatient, { currentData: gqlData }] =
    useLazyGetRoomForPatientQuery();

  const room = isGraphqlRoomQueriesEnabled
    ? gqlData?.Room?.getRoomForPatient
    : mapOgRoomToV2ForPatient(ogData);

  const roomSlug = new URLSearchParams(window.location.search).get('slug');

  useEffect(() => {
    if (roomSlug === null) {
      return;
    }

    if (isGraphqlRoomQueriesEnabled) {
      void getRoomForPatient({
        input: {
          slug: roomSlug,
          featureKeys: PATIENT_ROOM_FEATURE_KEYS,
        },
      });
    } else {
      void getRoomBySlug({ roomSlug });
    }
  }, [getRoomBySlug, roomSlug, getRoomForPatient]);
  /**
   * Temporary solution end.
   */

  const { getCheckInRedirectRoute } = useRedirect();
  const checkInRedirectRoute = getCheckInRedirectRoute(roomSlug);

  const dispatch = useAppDispatch();

  /**
   * Listen to the call start event and set the apolloSession with isV2 flag.
   */
  useEffect(() => {
    if (identityId === undefined || !room) {
      return () => undefined;
    }

    const handleMessage = (payload: CallStartMessagePayload): void => {
      const {
        sessionId,
        vendor,
        joinedParticipantsIds,
        isAudioCall,
        startCallTime,
        dbSessionId,
        joinToken,
        providerUserId,
        providerName,
      } = payload;

      const callDetails: Partial<ApolloSession> = {
        roomId: `${room.id}`,
        sessionId,
        identityId,
        vendor,
        joinedParticipantsIds,
        role: RealtimeRole.Patient,
        isV2: true,
        referrer: checkInRedirectRoute,
        navigationStarted: Date.now(),
        isAudioCall,
        startCallTime,
        dbSessionId,
      };

      dispatch(update(callDetails));

      waitingRoomTimer.finish(WaitingRoomStep.WaitingRoomRedirectToCall);
      void router.push(getPublicRuntimeConfig().paths.call);

      void callService.start({
        isAudioCall: Boolean(isAudioCall),
        identityId,
        sessionId,
        joinToken,
        providerUserId,
        providerName,
      });
    };

    const callback = (message: RealtimeMessage): void => {
      const payload = (message.payload ?? {}) as CallStartMessagePayload;
      handleMessage(payload);
    };

    const unsubscribe = subscribeToMessage({
      messageType: RealtimeMessageType.CallStart,
      callback,
    });

    const unsubscribeLegacy = subscribeToLegacyMessage({
      messageType: RealtimeMessageType.LegacyCallStart,
      callback,
    });

    return (): void => {
      unsubscribe();
      unsubscribeLegacy();
    };
  }, [identityId, room, checkInRedirectRoute, dispatch]);

  return {
    room,
  };
};
